import { useEffect, useRef, useState } from "react";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { DarshanImage } from "~/interface/daily-darshan/daily-darshan";
import { cn } from "~/lib/utils";
import { BLUR_HASH, FALLBACK_IMAGE_URL } from "~/utils/constant";
import RowGallery from "../shared-components/RowGallery";

interface DailyDarshanHomePhotosMobileProps {
  dailyDarshanData: DarshanImage[];
  selectedMandir: string;
  rowHeight: number;
}

function DailyDarshanHomePhotosMobile({ dailyDarshanData, selectedMandir, rowHeight }: DailyDarshanHomePhotosMobileProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(true);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScroll = () => {
    const container = scrollRef.current;
    if (container) {
      const { scrollLeft, scrollWidth, clientWidth } = container;
      // Show left arrow only if scrolled at least 150px from the left
      setCanScrollLeft(scrollLeft > 50);

      // Show right arrow only if not already scrolled to (almost) the end
      setCanScrollRight(scrollLeft + clientWidth < scrollWidth);
    }
  };

  const scroll = (direction: "left" | "right") => {
    const container = scrollRef.current;
    if (container) {
      const scrollAmount = 500; // 👈 adjust this value to your liking
      const value = direction === "left" ? -scrollAmount : scrollAmount;
      container.scrollBy({ left: value, behavior: "smooth" });
    }
  };

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    checkScroll();

    const handleResize = () => checkScroll();
    const handleScroll = () => checkScroll();

    container.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleResize);

    return () => {
      container.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ left: 0, behavior: "smooth" });
      // Reset scroll state when dailyDarshanData changes
      checkScroll();
    }
  }, [dailyDarshanData]);

  const showArrows = canScrollLeft || canScrollRight;

  return (
    <div className="w-full flex justify-center items-center relative max-lg:overflow-hidden px-3">
      {showArrows && (
        <div
          className={cn(
            "absolute -left-20 size-10 rounded-full flex justify-center items-center cursor-pointer",
            canScrollLeft ? "bg-darkOrangeColor" : "bg-secondaryColor",
            canScrollLeft ? "" : "cursor-not-allowed",
          )}
          onClick={() => scroll("left")}
          title="go back"
        >
          <IoIosArrowBack size={24} color={canScrollLeft ? "white" : "#e7c297"} />
        </div>
      )}

      <div
        className={cn("flex items-center overflow-x-auto scrollbar-hide relative", showArrows ? "justify-start" : "justify-center")}
        ref={scrollRef}
      >
        <RowGallery
          imageData={dailyDarshanData
            ?.sort((a, b) => {
              // Move YouTube videos (type === "yt") to the end
              if (a.type === "yt" && b.type !== "yt") return 1;
              if (a.type !== "yt" && b.type === "yt") return -1;
              return 0;
            })
            ?.map((item) => ({
              id: item.id,
              url:
                item.type === "image" && item?.img
                  ? {
                      small: item.img.s.url,
                      medium: item.img.m.url,
                      large: item.img.l.url,
                      hd: item.img.l.url,
                      original: item.img.og.url,
                    }
                  : {
                      small: FALLBACK_IMAGE_URL,
                      medium: FALLBACK_IMAGE_URL,
                      large: FALLBACK_IMAGE_URL,
                      hd: FALLBACK_IMAGE_URL,
                      original: FALLBACK_IMAGE_URL,
                    },
              alt: `${selectedMandir} ${item.date}`,
              blur_hash: item.img ? item.img.bh : BLUR_HASH,
              width: item.img ? item.img.ar * rowHeight : 200,
              height: rowHeight,
              video_id: item.type === "yt" ? item.original_url : null,
            }))}
          rowHeight={rowHeight}
          className="gap-4 !p-0"
        />
      </div>

      {showArrows && (
        <div
          className={cn(
            "absolute -right-20 size-10 rounded-full flex justify-center items-center cursor-pointer",
            canScrollRight ? "bg-darkOrangeColor" : "bg-secondaryColor",
            canScrollRight ? "" : "cursor-not-allowed",
          )}
          onClick={() => scroll("right")}
          title="go forward"
        >
          <IoIosArrowForward size={24} color={canScrollRight ? "white" : "#e7c297"} />
        </div>
      )}
    </div>
  );
}

export default DailyDarshanHomePhotosMobile;
